// Background-sync drain (, client half). Reads the encrypted offline
// queue (services/offline.ts), pushes each queued mutation to POST /api/sync,
// and reconciles the per-mutation outcomes:
//
//   applied / duplicate / conflict → terminal on the server, clear the local
//                                     copy (compare-and-delete so a newer edit
//                                     made during the round-trip survives)
//   rejected                       → left queued + reported, never silently
//                                     dropped (the caller can flag it for repair)
//
// Two trigger paths feed this one drain: the foreground `online` event + boot
// (the universal path, and the only one on iOS Safari), and the Background Sync
// `sync` event in the service worker, which wakes the page to drain on reconnect
// where supported. Both funnel here; `draining` serialises them so overlapping
// triggers never double-send (and even if they did, the server's receipts make
// a re-send an idempotent `duplicate`).

import { ApiError } from './api';
import { postSync, type SyncMutation, type SyncResult } from '../api/client';
import { listPending, removeIfSynced, type PendingMutation } from './offline';

// Mirrors the server's offlinesync.MaxBatch — split a larger queue across calls.
const MAX_BATCH = 200;

// The Background Sync registration tag, shared with the service worker handler.
export const SYNC_TAG = 'sync-mutations';

let draining = false;

// DrainResult summarises one drain pass for the caller to surface. applied,
// duplicate, and conflict are counts of cleared records; rejected carries the
// per-mutation results that stayed queued so the UI can flag them.
export interface DrainResult {
  applied: number;
  duplicate: number;
  conflict: number;
  rejected: SyncResult[];
}

// toMutation maps a decrypted pending record to the /api/sync wire shape. The
// epoch-ms updatedAt becomes an RFC3339 string — the server's last-write-wins
// clock. kind is already the shared offline-kind union; module is widened to
// string in storage, so it is narrowed back here (the server re-validates it).
function toMutation(p: PendingMutation): SyncMutation {
  return {
    clientMutationId: p.clientMutationId,
    kind: p.kind,
    module: p.module as SyncMutation['module'],
    rawId: p.rawId,
    updatedAt: new Date(p.updatedAt).toISOString(),
    // The client has no tombstone flow yet (no delete UI until the form modules);
    // every queued record is a create/update. Client-initiated deletes are a
    // follow-up that will set this from the record.
    deleted: false,
    payload: p.payload as Record<string, unknown>,
  };
}

// drainSyncQueue pushes every queued mutation for workerId to the server and
// reconciles the outcomes. Returns the per-pass summary, or null when the drain
// was skipped (offline, already draining, nothing queued) or aborted by a
// transient/auth failure (the queue is left intact for the next trigger).
export async function drainSyncQueue(workerId: string): Promise<DrainResult | null> {
  if (draining || !navigator.onLine) return null;
  draining = true;
  try {
    const pending = await listPending(workerId);
    if (pending.length === 0) return null;

    const summary: DrainResult = { applied: 0, duplicate: 0, conflict: 0, rejected: [] };
    for (let i = 0; i < pending.length; i += MAX_BATCH) {
      const chunk = pending.slice(i, i + MAX_BATCH);
      const byId = new Map(chunk.map((p) => [p.clientMutationId, p]));

      let resp;
      try {
        resp = await postSync({ mutations: chunk.map(toMutation) });
      } catch (err) {
        // 503 SYNC_UNAVAILABLE, a network drop, or the compromise 401 (api()
        // already triggered the wipe + redirect): stop here and leave this chunk
        // and the rest queued. The next online event / boot retries; receipts
        // make the retry idempotent. A non-ApiError is an unexpected fault —
        // surface it.
        if (err instanceof ApiError) return summary;
        throw err;
      }

      for (const r of resp.results) {
        const p = byId.get(r.clientMutationId);
        if (!p) continue;
        if (r.status === 'rejected') {
          // Not silently dropped: leave the record queued and report it.
          summary.rejected.push(r);
          continue;
        }
        // applied | duplicate | conflict — terminal on the server. Clear the
        // local copy, but only if it is still the same mutation (a re-edit
        // during the round-trip minted a new clientMutationId and must survive).
        await removeIfSynced(p.kind, p.rawId, p.clientMutationId);
        if (r.status === 'applied') summary.applied++;
        else if (r.status === 'duplicate') summary.duplicate++;
        else summary.conflict++;
      }
    }
    return summary;
  } finally {
    draining = false;
  }
}

// requestBackgroundSync registers the one-shot sync so the browser drains on
// reconnect even if the tab is backgrounded (Chromium/Android). A no-op where
// unsupported — notably iOS Safari, which has no Background Sync and relies on
// the foreground online-event drain. Callers register this right after queueing
// a mutation while offline.
export async function requestBackgroundSync(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg.sync) {
      await reg.sync.register(SYNC_TAG);
    }
  } catch {
    // Unsupported or permission-denied — the foreground drain covers it.
  }
}
